import { Component, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { EntityKey } from '../../entity/entity';
import { DocumentService } from '../../services/document-service';
import { UploadFile } from '../../documents/upload-file';
import { TagService } from '../../services/tag-service';
import { TagSaveResponse, MessageLevel } from '../../tags/tag-save-response';
import { FileUtils } from '../../utils/file-utils';
import { HttpErrorResponse } from '@angular/common/http';
import { StringUtils } from '../../utils/string-utils';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogActions } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgClass } from '@angular/common';

@Component({
  selector: 'tagging-dialog',
  templateUrl: './tagging-dialog.component.html',
  styleUrls: ['./tagging-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatProgressSpinner, NgClass, MatDialogActions, MatButton],
})
export class TaggingDialogComponent {
  private readonly tagService = inject(TagService);
  private readonly documentService = inject(DocumentService);
  dialogRef = inject<MatDialogRef<TaggingDialogComponent>>(MatDialogRef);
  private readonly data = inject(MAT_DIALOG_DATA);

  entityKey: EntityKey;
  readonly isBusy = signal<boolean>(undefined!);
  uploadFile!: UploadFile;
  readonly saveResponse = signal<TagSaveResponse>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly messages = signal<string[]>(undefined!);

  constructor() {
    this.messages.set([]);
    this.entityKey = this.data.entityKey as EntityKey;
    this.messages.update(current => [...current, 'Please select a file to upload']);
    this.messages.update(current => [...current, 'Please download a template to get started']);
  }

  getTemplate(): void {
    this.isBusy.set(true);
    this.tagService.getTagTemplate(this.entityKey).subscribe({
      next: (result: Blob) => {
        FileUtils.downloadFile(result, this.entityKey.entityId + '-tags.xlsx');
        this.isBusy.set(false);
        this.messages.update(current => [...current, "Please make appropriate changes to the downloaded file and click the 'Select File' button to upload"]);
      },
      error: (error: any) => this.handleError(error),
    });
  }

  fileChange(upload: any): void {
    this.saveResponse.set(undefined!);
    this.isBusy.set(true);
    let reader = new FileReader();
    this.messages.set([]);
    if (upload.target.files && upload.target.files.length > 0) {
      const file = upload.target.files[0];
      reader.readAsArrayBuffer(file);
      reader.onload = () => {
        let mimeType = file.type || this.getMimeType(file.name);
        this.uploadFile = new UploadFile();
        this.uploadFile.documentName = file.name;
        this.uploadFile.entityKey = this.entityKey;
        this.uploadFile.fileBytes = reader.result as unknown as Uint8Array;
        this.uploadFile.fileName = file.name;
        this.uploadFile.mimeType = mimeType;
        this.uploadFile.documentType = 'TAG_CHART';
        this.isBusy.set(false);
        this.messages.update(current => [...current, "Please click the 'Save Tags' button to update the tag values"]);
      };
    }
  }

  private getMimeType(fileName: string): string {
    let mimeType: string = 'text/plain';
    if (fileName && fileName.length > 2) {
      switch (fileName.slice(-3).toLowerCase()) {
        case 'eml':
          mimeType = 'message/rfc822';
          break;
      }
    }
    return mimeType;
  }

  saveTagging(): void {
    this.messages.set([]);
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.tagService.saveTags(this.entityKey, this.uploadFile.fileBytes).subscribe({
      next: (result: TagSaveResponse) => {
        this.messages.set(result.messages);
        this.saveResponse.set(result);
        if (this.saveResponse().level == MessageLevel.INFO) {
          let uploadFiles: UploadFile[] = [];
          uploadFiles.push(this.uploadFile);
          this.documentService.uploadFiles(uploadFiles).subscribe({
            next: () => {
              this.messages.update(current => [...current, 'Success!']);
              this.dialogRef.close(this.entityKey);
            }, error: (error: any) => this.handleError(error),
          });
        } else {
          this.isBusy.set(false);
        }
      }, error: (error: any) => this.handleError(error)
    });
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  isReadyToUpload(): boolean {
    return this.uploadFile != null;
  }

  showOverride(): boolean {
    return this.saveResponse() && this.saveResponse().level == MessageLevel.WARNING;
  }

  getMessageClass(): string {
    if (this.saveResponse()) {
      switch (this.saveResponse().level) {
        case MessageLevel.ERROR:
          return 'error';
        case MessageLevel.INFO:
          return 'info';
        case MessageLevel.WARNING:
          return 'warn';
      }
    } else {
      return 'info';
    }
  }
}
